import CircularProgress from "@mui/material/CircularProgress";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useContext, useRef, useState } from "react";
import Confetti from "react-confetti";
import { Severity, SnackbarContext } from "src/contexts/SnackbarContext";
import { useGeolocation } from "src/hooks/useGeolocation";
import BingoBoard from "./BingoBoard";
import { BingoResolution } from "./BingoResolution";
import ZoomedBingoSquare from "./ZoomedBingoSquare";
import {
  fetchBingoResolutions,
  updateBingoResolution,
  UpdateBingoResolutionRequest,
  updateBingoResolutionTally,
  UpdateBingoResolutionTallyRequest,
} from "./api";
import { LastUpdatedAt } from "./utils";

enum ViewMode {
  BOARD,
  SQUARE,
}

interface BingoContainerProps {
  year: string;
  user: string;
}

export default function BingoContainer({ year, user }: BingoContainerProps) {
  const confettiRef = useRef(null);
  const [displayConfetti, setDisplayConfetti] = useState(false);
  const [locationData] = useGeolocation();
  const { showSnackbar } = useContext(SnackbarContext);
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(
    ViewMode.BOARD,
  );
  const [selectedResolution, setSelectedResolution] =
    useState<BingoResolution | null>(null);

  const { data: resolutions, refetch: refetchResolutions } = useQuery({
    queryKey: ["fetchBingoResolutions", year, user],
    queryFn: async () => {
      return fetchBingoResolutions(user, year).then((res) => res.data || []);
    },
  });

  function getBingoResolution(resolutionId: string): BingoResolution | null {
    if (!resolutions) return null;

    const resolution = resolutions.find((res) => res.id === resolutionId);
    return resolution || null;
  }

  const updateBingoResolutionTallyMutation = useMutation({
    mutationFn: (mutationData: UpdateBingoResolutionTallyRequest) => {
      return updateBingoResolutionTally(mutationData);
    },
  });

  const updateBingoResolutionMutation = useMutation({
    mutationFn: (mutationData: UpdateBingoResolutionRequest) => {
      return updateBingoResolution(mutationData);
    },
  });

  function onBingoSquareClick(resolutionId: string) {
    setCurrentViewMode(ViewMode.SQUARE);
    setSelectedResolution(getBingoResolution(resolutionId));
  }

  async function onBingoSquareIncrement() {
    if (!selectedResolution) return;

    const newTallyValue = selectedResolution.currentTally + 1;
    await handleUpdateResolutionTally(selectedResolution.id, newTallyValue);

    // If you just met a goal, display confetti
    if (newTallyValue === selectedResolution.goalTally) {
      setDisplayConfetti(true);
      setTimeout(() => {
        setDisplayConfetti(false);
      }, 10_000 /* 10 seconds */);
    }
  }

  async function onBingoSquareDecrement() {
    // Ensure we have a selected resolution
    if (!selectedResolution) return;

    const newTallyValue = Math.max(0, selectedResolution.currentTally - 1);
    await handleUpdateResolutionTally(selectedResolution.id, newTallyValue);
  }

  async function handleUpdateResolutionTally(
    resolutionId: string,
    newTally: number,
  ) {
    // First, change locally. This is important for updating the background color when the goal is reached.
    if (selectedResolution) {
      const copyOfSelectedResolution = { ...selectedResolution };
      copyOfSelectedResolution.currentTally = newTally;
      setSelectedResolution(copyOfSelectedResolution);
    }

    // Then, update in database
    const updateBingoResolutionTallyRequest: UpdateBingoResolutionTallyRequest =
      {
        resolutionId: resolutionId,
        newTally: newTally,
        locationData,
      };

    await updateBingoResolutionTallyMutation.mutateAsync(
      updateBingoResolutionTallyRequest,
      {
        onSuccess: async (data) => {},
        onError: (data) => {
          showSnackbar({
            message: "Error updating tally",
            severity: Severity.ERROR,
          });
          console.error("Error updating bingo resolution tally:", data);
        },
      },
    );

    // Refresh resolutions
    await refetchResolutions();
  }

  async function onBingoSquareEdit(
    resolutionId: string,
    newTitle: string,
    newGoalTally: number,
    newCurrentTally: number,
    newColor: string,
    newNote: string,
  ) {
    // First, change locally
    if (selectedResolution) {
      selectedResolution.title = newTitle;
      selectedResolution.goalTally = newGoalTally;
      selectedResolution.currentTally = newCurrentTally;
      selectedResolution.color = newColor;
      selectedResolution.note = newNote;
    }

    // Then, update in database
    const updateBingoResolutionRequest: UpdateBingoResolutionRequest = {
      resolutionId,
      newTitle,
      newGoalTally,
      newCurrentTally,
      newColor,
      newNote,
      locationData,
    };

    await updateBingoResolutionMutation.mutateAsync(
      updateBingoResolutionRequest,
      {
        onSuccess: async (data) => {},
        onError: (data) => {
          showSnackbar({
            message: "Error updating resolution",
            severity: Severity.ERROR,
          });
          console.error("Error updating bingo resolution:", data);
        },
      },
    );

    // Refresh resolutions
    await refetchResolutions();
  }

  if (!resolutions) {
    return <CircularProgress />;
  }

  return (
    <>
      {displayConfetti && <Confetti ref={confettiRef} />}
      {resolutions && resolutions.length >= 0 && (
        // Hides the "Last updated" text in square view without shrinking the div.
        <div
          className={`flex justify-center pb-2 ${currentViewMode === ViewMode.SQUARE && "text-white"}`}
        >
          {LastUpdatedAt({
            updatedAt: new Date(
              Math.max(...resolutions.map((res) => res.updatedAt.getTime())),
            ),
          })}
        </div>
      )}
      {currentViewMode === ViewMode.BOARD && (
        <BingoBoard
          onBingoSquareClick={onBingoSquareClick}
          resolutions={resolutions}
        />
      )}
      {currentViewMode === ViewMode.SQUARE && selectedResolution && (
        <ZoomedBingoSquare
          bingoResolution={selectedResolution}
          onIncrement={() => onBingoSquareIncrement()}
          onDecrement={() => onBingoSquareDecrement()}
          onEdit={(
            newTitle,
            newGoalTally,
            newCurrentTally,
            newColor,
            newNote,
          ) =>
            onBingoSquareEdit(
              selectedResolution.id,
              newTitle,
              newGoalTally,
              newCurrentTally,
              newColor,
              newNote,
            )
          }
          onClose={() => setCurrentViewMode(ViewMode.BOARD)}
        />
      )}
    </>
  );
}
